import { Typography } from "@mui/material";
import { transicon } from "@platformx/shared/static-assets";
import { ratios } from "@platformx/utilities";
import { memo } from "react";
import { Cropper } from "react-advanced-cropper";
import "react-advanced-cropper/dist/style.css";

const cropperStyle = {
  height: "400px",
  background: `url(${transicon})`,
};

/**
 * Converts a saved 4-point crop vertex array into the {left, top, width, height} format
 * expected by react-advanced-cropper's `defaultCoordinates` prop.
 *
 * Vertex order: [Top-Left, Top-Right, Bottom-Left, Bottom-Right]
 * Returns `undefined` when the vertex data is empty or produces a zero-size box
 * (so the cropper falls back to its built-in default position).
 */
const getDefaultCoordinates = (initialCrop: any[] = []) => {
  if (initialCrop.length < 4) {
    return undefined;
  }

  const [{ x: left = 0, y: top = 0 }, { x: right = left }, { y: bottom = top }] = initialCrop;
  const width = Math.max(right - left, 0);
  const height = Math.max(bottom - top, 0);

  if (!width || !height) {
    return undefined;
  }

  return {
    left,
    top,
    width,
    height,
  };
};

const SelectedImageCrop = (props: any = {}) => {
  const {
    crop = "",
    aspect = 0,
    ratio = "",
    imageSrc = "",
    onCropChange,
    isLoading = true,
    setIsLoading,
    initialCrop,
  } = props;
  // Compute stable initial coordinates from the saved crop vertices.
  // These are only recalculated when the dialog reopens with new saved data,
  // NOT on every drag update — which prevents remounting the cropper during editing.
  const defaultCoordinates = getDefaultCoordinates(initialCrop);

  // Build a stable key from the saved crop position so the Cropper remounts
  // only when saved data changes (dialog reopen), not during live drag events.
  const cropKey = defaultCoordinates
    ? `${crop}-${defaultCoordinates.left}-${defaultCoordinates.top}-${defaultCoordinates.width}-${defaultCoordinates.height}`
    : crop;

  // Convert the cropper's current stencil position into the 4-point vertex format
  // used by the CDN crop API: [Top-Left, Top-Right, Bottom-Left, Bottom-Right]
  const onChange = (cropper: any) => {
    const { height = 0, left = 0, top = 0, width = 0 } = cropper.getCoordinates() || {};
    const cropVertex = [
      {
        x: left,
        y: top,
      },
      {
        x: width + left,
        y: top,
      },
      {
        x: left,
        y: height + top,
      },
      {
        x: width + left,
        y: height + top,
      },
    ];
    onCropChange(cropVertex);
  };

  const onError = (cropper: any) => {
    console.error("Image is not loading", cropper);
    setIsLoading(false);
  };

  const onReady = () => {
    if (isLoading) setIsLoading(false);
  };

  // Make the cropper fill the full visible area by default when no saved crop exists
  const defaultSize = ({ imageSize, visibleArea }: { imageSize: any; visibleArea: any }) => {
    return {
      width: (visibleArea || imageSize).width,
      height: (visibleArea || imageSize).height,
    };
  };
  return (
    // key on both the wrapper and the Cropper ensures a clean remount when saved crop data changes
    <div className='crop-container' key={cropKey}>
      <Cropper
        key={cropKey}
        src={imageSrc}
        stencilProps={{
          aspectRatio: aspect,
        }}
        defaultCoordinates={defaultCoordinates}
        defaultSize={defaultSize}
        onChange={onChange}
        onReady={onReady}
        onError={onError}
        style={cropperStyle}
      />
      <Typography
        variant='h4bold'
        sx={{ padding: { xs: "5px 0", md: "14px 0" }, textAlign: "center" }}>
        {ratios[ratio.toLowerCase()] ? `Aspect Ratio: ${ratios[ratio.toLowerCase()]}` : ratio}
      </Typography>
    </div>
  );
};

export default memo(SelectedImageCrop);
